import React from 'react';
import styled from 'styled-components';
import {Button, Heading, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';

const tokens = tokensForThemeProvider.sky.sys;

/* A plain div rather than the DS FlexColumn, matching `EmptyReportState`: that component
 * destructures `data-cy` and then never renders it, so the Cypress hook below would silently never
 * reach the DOM. The layout it provided is two lines of CSS. */
const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: ${tokens.spacing['200']};
	padding: ${tokens.spacing['800']} ${tokens.spacing['400']};
`;

/** Shown in place of the table when the data fetch rejected (non-2xx or network failure).
 * Deliberately distinct from `EmptyReportState`: that one tells the user the report ran and matched
 * nothing, which would be a lie here — we do not know how many tasks match. Offers a retry because
 * the fetch is otherwise only re-run when the definition changes.
 *
 * `accessDenied` switches it to the one failure a retry cannot clear: the server refusing this
 * viewer the data. That case says so plainly and drops the retry button entirely — offering a
 * button whose only possible outcome is the same refusal wastes the user's time and reads as a
 * transient glitch rather than a permissions boundary. */
export function ReportErrorState({onRetry, accessDenied = false}: {onRetry: () => void; accessDenied?: boolean}): JSX.Element {
	const {formatMessage} = useIntl();
	// The two cases differ only by which pair of messages they read and whether a retry is offered,
	// so the shell is written once — a change to it cannot then apply to one case and not the other.
	const prefix = accessDenied ? 'ai_report_builder.error.no_access' : 'ai_report_builder.error';

	return (
		<Wrapper data-cy={'ai-report-builder-error'}>
			<Heading size={'3'}>{formatMessage({id: `${prefix}.title`})}</Heading>
			<Text>{formatMessage({id: `${prefix}.body`})}</Text>
			{accessDenied ? null : (
				<Button emphasis={'medium'} onPress={onRetry} data-cy={'ai-report-builder-retry'}>
					{formatMessage({id: 'ai_report_builder.error.retry'})}
				</Button>
			)}
		</Wrapper>
	);
}
